/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Stack from "@mui/material/Stack";
import { Grid2, Container } from "@mui/material";
import PropTypes from "prop-types";
import "./ApplicationDetailsTab.css";

// ============ HELPER FUNCTIONS (Pure, No Side Effects) ============

const isNonEmpty = (val) => val !== null && val !== "";

const validateDescriptions = (descriptions, appData) => {
  const hasAllDescriptions =
    descriptions.every((desc) => isNonEmpty(desc.applicationName));

  const hasValidDescriptions = descriptions.every(
    (desc) =>
      isNonEmpty(desc.longDescription) &&
      isNonEmpty(desc.shortDescription) &&
      (appData?.submissions?.length <= 1 || isNonEmpty(desc.whatIsNew))
  );

  const hasScreenshotsAndLinks = descriptions.every(
    (desc) =>
      (sessionStorage.getItem("idpLogin") === "true" ||
        sessionStorage.getItem("isOemSeller") ||
        (desc.customerSupportLink !== null && desc.customerSupportLink !== "")) &&
      desc.screenshots.length > 0
  );

  return { hasAllDescriptions, hasValidDescriptions, hasScreenshotsAndLinks };
};

const shouldShowLegalDocs = (submissionData, descriptions) => {
  if (
    sessionStorage.getItem("idpLogin") === "true" ||
    sessionStorage.getItem("isOemSeller")
  ) {
    return true;
  }

  const languages = descriptions.map((desc) => desc.language);
  for (const lang of languages) {
    const legalLinks =
      submissionData?.legalDocuments?.legalLinkInfos?.[lang]?.legalLinks;
    if (legalLinks === undefined || legalLinks["Privacy Policy"] === null) {
      return false;
    }
  }
  return true;
};

const calculateAppInfoComplete = (submissionData, descriptions, appData) => {
  const categoryValue = !!(
    submissionData.categoryIds && submissionData.categoryIds.length > 0
  );

  const { hasAllDescriptions, hasValidDescriptions } = validateDescriptions(
    descriptions,
    appData
  );

  const hasBinaryOrBundle =
    (submissionData.binary && Object.keys(submissionData.binary).length > 0) ||
    (submissionData.bundle && Object.keys(submissionData.bundle).length > 0);

  if (!categoryValue || !hasAllDescriptions || !hasValidDescriptions || !hasBinaryOrBundle) {
    return false;
  }

  if (appData?.submissions?.length > 1) {
    return descriptions.every((desc) => isNonEmpty(desc.whatIsNew));
  }

  if (appData?.submissions?.length === 1) {
    if (appData.submissions[0].id === submissionData.id) {
      return hasValidDescriptions;
    }
    return descriptions.every((desc) => isNonEmpty(desc.whatIsNew));
  }

  return true;
};

const calculateLicenseComplete = (submissionData) => {
  if (
    sessionStorage.getItem("idpLogin") === "true" ||
    sessionStorage.getItem("isOemSeller")
  ) {
    if (
      submissionData.enableServiceIds === false &&
      submissionData.enableProductIds === false
    ) {
      return true;
    }

    let licenseTrue = true;
    if (submissionData.enableServiceIds === true) {
      licenseTrue =
        submissionData.associatedServiceIds &&
        submissionData.associatedServiceIds.length > 0;
    }
    if (submissionData.enableProductIds === true) {
      licenseTrue =
        licenseTrue &&
        submissionData.associatedProductIds &&
        submissionData.associatedProductIds.length > 0;
    }
    return licenseTrue;
  }
  return true;
};

const calculateTargetingComplete = (submissionData) => {
  const targeting = { ...submissionData.targeting };
  return (
    targeting.countriesInclusions?.length > 0 &&
    (sessionStorage.getItem("idpLogin") === "true" ||
      sessionStorage.getItem("isOemSeller") ||
      targeting.architectureTypesInclusions?.length > 0) &&
    targeting.vehiclesInclusions?.length > 0
  );
};

function ApplicationDetailsTab({ checkAllTabsAreTrue }) {
  const [tabs, setTabs] = useState([
    { name: "App Info", active: false, complete: false },
    {
      name:
        sessionStorage.getItem("idpLogin") === "true"
          ? "Screenshots"
          : "Service Links & Screenshots",
      active: false,
      complete: false,
    },
    { name: "Pricing", active: false, complete: true },
    { name: "Additional Info", active: false, complete: true },
    { name: "Settings", active: false, complete: false },
  ]);
  const hasPaymentRole =
    sessionStorage
      .getItem("payment_role")
      .includes("IGNITESTOREMONETIZE_SCP") ||
    sessionStorage
      .getItem("payment_role")
      .includes("IGNITESTOREMONETIZEPREMIUM_SCP")

  const submissionData = useSelector(
    (state) => state.submissionDetails.submissionDetails
  );
  const appData = useSelector(
    (state) => state.applicationDetails.applicationDetails
  );
  const binaryDetails = useSelector((state) => state.binaryDetails);

  useEffect(() => {
    if (!submissionData || Object.keys(submissionData).length === 0) {
      return;
    }

    const newTabs = [...tabs];
    const descriptions = submissionData.descriptions || [];

    // Check existence and basic requirements
    if (!hasPaymentRole) {
      const pricingIndex = newTabs.findIndex((item) => item.name === "Pricing");
      if (pricingIndex !== -1) {
        newTabs.splice(pricingIndex, 1);
      }
    }

    // Calculate tab completion states
    newTabs[0].active = true;
    newTabs[0].complete = calculateAppInfoComplete(submissionData, descriptions, appData);

    const { hasScreenshotsAndLinks } = validateDescriptions(descriptions, appData);
    const screenshotsLegal = hasScreenshotsAndLinks && shouldShowLegalDocs(submissionData, descriptions);
    newTabs[1].complete = !!screenshotsLegal;

    const allLicense = calculateLicenseComplete(submissionData);
    const targetingComplete = calculateTargetingComplete(submissionData);

    if (hasPaymentRole) {
      newTabs[4].complete = targetingComplete;
    } else {
      newTabs[3].complete = targetingComplete;
    }

    setTabs(newTabs);

    // Check all tabs completion
    const allTabsBoolean = newTabs.every((tab) => tab.complete) && allLicense;
    checkAllTabsAreTrue(allTabsBoolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appData, submissionData, binaryDetails, hasPaymentRole]);

  useEffect(() => {}, [tabs]);

  useEffect(() => {
    let completedTabs = 0;
    tabs.forEach((tab) => {
      if (tab.complete) {
        completedTabs++;
      }
    });
  }, [tabs]);

  const changeTabName = (tabname, index) => {
    let newTabs = [...tabs];
    newTabs.forEach((ele) => {
      ele.active = false;
    });
    newTabs[index].active = true;
    setTabs([...newTabs]);
    switch (tabname) {
      case "App Info":
        scrollId("app-info");
        break;
      case "Descriptions":
        scrollId("description");
        break;
      case "Service Links & Screenshots":
        scrollId("service-links");
        break;
      case "Screenshots":
        scrollId("screenshots");
        break;
      case "Additional Info":
        scrollId("additonalInformation");
        break;
      case "Settings":
        scrollId("targeting");
        break;
      case "Pricing":
        scrollId("monetizeApplication");
        break;
      default:
        break;
    }
  };

  const scrollId = (id) => {
    setTimeout(() => {
      const el = document.getElementById(id);
      const yOffset = -290;
      const y = el.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }, 100);
  };

  return (
    <Container maxWidth="lg">
      <Grid2 container justifyContent="flex-start" alignItems="center">
        <Grid2 size={12}>
          <section className="tabs-section-draft">
            <Stack
              direction="row"
              spacing={3}
              sx={{
                marginLeft: "10%",
                borderBottom: "1px solid  #12141533",
                width: "84.6%",
                background: "white !important",
              }}
            >
              {tabs.map((tab, index) => {
                const tabId = `${tab.name}-${index}`;
                const handleKeyDown = (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    changeTabName(tab.name, index);
                  }
                };
                return (
                  <button
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      width: "100%",
                    }}
                    onClick={() => changeTabName(tab.name, index)}
                    onKeyDown={handleKeyDown}
                    key={tabId}
                    type="button"
                    className={
                      tab.active
                        ? "tabname-draft-index active"
                        : "tabname-draft-index not-active"
                    }
                  >
                    <span className={tab.active ? "round" : "not-round"}>
                      {index + 1}
                    </span>
                    <span>{tab.name}</span>
                    {
                      <div
                        style={
                          tab.complete
                            ? {
                                Visibility: "visible",
                                display: "flex",
                                alignItems: "center",
                                marginLeft: "6px",
                              }
                            : {
                                visibility: "hidden",
                              }
                        }
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8.6 14.6L15.65 7.55L14.25 6.15L8.6 11.8L5.75 8.95L4.35 10.35L8.6 14.6ZM10 20C8.61667 20 7.31667 19.7375 6.1 19.2125C4.88333 18.6875 3.825 17.975 2.925 17.075C2.025 16.175 1.3125 15.1167 0.7875 13.9C0.2625 12.6833 0 11.3833 0 10C0 8.61667 0.2625 7.31667 0.7875 6.1C1.3125 4.88333 2.025 3.825 2.925 2.925C3.825 2.025 4.88333 1.3125 6.1 0.7875C7.31667 0.2625 8.61667 0 10 0C11.3833 0 12.6833 0.2625 13.9 0.7875C15.1167 1.3125 16.175 2.025 17.075 2.925C17.975 3.825 18.6875 4.88333 19.2125 6.1C19.7375 7.31667 20 8.61667 20 10C20 11.3833 19.7375 12.6833 19.2125 13.9C18.6875 15.1167 17.975 16.175 17.075 17.075C16.175 17.975 15.1167 18.6875 13.9 19.2125C12.6833 19.7375 11.3833 20 10 20Z"
                            fill="#00B05D"
                          />
                        </svg>
                      </div>
                    }
                  </button>
                );
              })}
            </Stack>
          </section>
        </Grid2>
      </Grid2>
    </Container>
  );
}

ApplicationDetailsTab.propTypes = {
  checkAllTabsAreTrue: PropTypes.func.isRequired,
};

export default ApplicationDetailsTab;
